import { RAReportDataSource } from '@/constants/ReportDashboard/cards-enums'
import { IGfpaFactorContributionFreq, IGfpaFactorContributionTotal, IGfpaSecurityContrib } from '@/interfaces/gfpaReport.interface'
import { pickFactorFrequency } from '@/utils/factorAttributionUtils'
import raDashboardDataService, { AttributionAPIResult, AttributionDurationData, AttributionGfpaSummaryDatum, AttributionTotalReturnsData } from '@/services/report-dashboard/raDashboard.data.service'
import dayjs, { Dayjs } from 'dayjs'
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'

export interface FAAtrributionData {
  "totalReturn": AttributionTotalReturnsData | null
  "attrDurations": AttributionDurationData | null
  "dailySummaries": Record<string, AttributionGfpaSummaryDatum> | null
  "factorContributionFreq": IGfpaFactorContributionFreq[] | null
  "factorContributionFreqZoom": IGfpaFactorContributionFreq[] | null
  "factorContributionTotal": IGfpaFactorContributionTotal[] | null
  "factorContributionTotalZoom": IGfpaFactorContributionTotal[] | null
  "securityContribution": IGfpaSecurityContrib[] | null
}

export interface FAAtrributionDataInformation {
  "reportId": number | null
  "portfolioName": string | null
  "loading": boolean
  "dataStore": FAAtrributionData | null
  "factorFreqError": boolean
  "securityError": boolean
  "beginDate": Dayjs | null
  "endDate": Dayjs | null
  "zoomPeriod": null | {
    "start": Dayjs
    "end": Dayjs
  }
  "zoomLoading": boolean
  "zoomError": boolean
  "setZoomPeriod": (period: null | { "start": Dayjs, "end": Dayjs }) => void
}

const emptyData: FAAtrributionData = {
  totalReturn: null,
  attrDurations: null,
  dailySummaries: null,
  factorContributionFreq: null,
  factorContributionFreqZoom: null,
  factorContributionTotal: null,
  factorContributionTotalZoom: null,
  securityContribution: null
}

const initialValue: FAAtrributionDataInformation = {
  reportId: null,
  portfolioName: null,
  loading: false,
  dataStore: null,
  factorFreqError: false,
  securityError: false,
  beginDate: null,
  endDate: null,
  zoomPeriod: null,
  zoomLoading: false,
  zoomError: false,
  setZoomPeriod: () => {}
};

export const FAAttributionDataContext = React.createContext(initialValue);

export default function FAAttributionDataContextProvider(
  props: PropsWithChildren<{
    reportId: number | null,
    portfolioName: string | null,
    beginDate: string | null,
    endDate: string | null,
    dataSources: Set<RAReportDataSource>
  }>,
) {
  const { reportId, portfolioName, dataSources } = props;
  const [attributionData, setAttributionData] = useImmer<FAAtrributionData>(emptyData);
  const [loading, setLoading] = useState<boolean>(false);
  const [factorFreqError, setFactorFreqError] = useState<boolean>(false);
  const [securityError, setSecurityError] = useState<boolean>(false);
  const [zoomPeriod, setZoomPeriod] = useState<null | { "start": Dayjs, "end": Dayjs }>(null);
  const [zoomLoading, setZoomLoading] = useState<boolean>(false);
  const [zoomError, setZoomError] = useState<boolean>(false);

  // Set loading optimistically so the chart shows a spinner the instant a zoom range is picked.
  const updateZoomPeriod = useCallback((period: null | { "start": Dayjs, "end": Dayjs }) => {
    setZoomPeriod(period);
    setZoomLoading(period != null);
    setZoomError(false);
  }, []);

  const beginDate = useMemo(() => {
    if (!props.beginDate) return null;
    const d = dayjs(props.beginDate);
    return d.isValid() ? d : null;
  }, [props.beginDate]);

  const endDate = useMemo(() => {
    if (!props.endDate) return null;
    const d = dayjs(props.endDate);
    return d.isValid() ? d : null;
  }, [props.endDate]);

  const info = useMemo(() => ({
    loading, reportId, portfolioName, beginDate, endDate, zoomPeriod, zoomLoading, zoomError, factorFreqError, securityError,
    setZoomPeriod: updateZoomPeriod,
    dataStore: attributionData
  }), [loading, attributionData, reportId, portfolioName, beginDate, endDate, zoomPeriod, zoomLoading, zoomError, factorFreqError, securityError, updateZoomPeriod]);
  
  useEffect(() => {
    // The backend rejects an equal beginDate/endDate pair too, not just an inverted one.
    if (reportId == null || beginDate == null || endDate == null || !beginDate.isBefore(endDate)) {
      setAttributionData(emptyData);
      setLoading(false);
      setFactorFreqError(false);
      setSecurityError(false);
      return;
    }
    // Cancel the data save if the effect is rerun while data is being fetched.
    let cancelled = false;
    setLoading(true);
    setFactorFreqError(false);
    setSecurityError(false);
    raDashboardDataService.setReportId(reportId);
    const apiPromises: Promise<AttributionAPIResult<unknown> | null>[] = [];
    dataSources.forEach(src => {
      let apiCall: Promise<AttributionAPIResult<unknown> | null> | undefined;
      switch (src) {
        case RAReportDataSource.TotalReturns:
          apiCall = raDashboardDataService.getDateRangeTotalReturn(beginDate, endDate);
          break;
        case RAReportDataSource.AttributionDurations:
          apiCall = raDashboardDataService.getDateRangeDuration(beginDate, endDate);
          break;
        case RAReportDataSource.DailySummaries:
          apiCall = raDashboardDataService.getGfpaSummaries(beginDate, endDate);
          break;
        case RAReportDataSource.FactorContributionFreq:
          apiCall = raDashboardDataService.getGfpaFactorContributionWithFreq(
            beginDate,
            endDate,
            pickFactorFrequency(beginDate, endDate),
          );
          break;
        case RAReportDataSource.FactorContributionTotal:
          apiCall = raDashboardDataService.getGfpaFactorContribution(beginDate, endDate);
          break;
        case RAReportDataSource.SecurityContribution:
          apiCall = raDashboardDataService.getGfpaSecurityDetails(beginDate, endDate);
          break;
      }
      if (apiCall !== undefined) {
        apiPromises.push(apiCall.then(val => { if (!cancelled) handleDataLoaded(val); return val; }));
      }
    });
    Promise.all(apiPromises).finally(() => {
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [reportId, beginDate, endDate, dataSources]);

  // A new report or a new base range invalidates any zoom.
  useEffect(() => { updateZoomPeriod(null); }, [reportId, beginDate, endDate, updateZoomPeriod]);

  // Zoom: refetch the factor contribution for the selected sub-range — the per-frequency data (chart)
  // and the totals (strip/table) — kept in their own fields so clearing the zoom restores the
  // full-range data instantly (no refetch).
  useEffect(() => {
    if (reportId == null || zoomPeriod == null) {
      setAttributionData(cache => {
        cache.factorContributionFreqZoom = null;
        cache.factorContributionTotalZoom = null;
      });
      setZoomLoading(false);
      setZoomError(false);
      return;
    }
    let cancelled = false;
    setZoomLoading(true);
    setZoomError(false);
    // Drop any prior zoom rows so a zoom-within-zoom shows the spinner instead of stale numbers.
    setAttributionData(cache => {
      cache.factorContributionFreqZoom = null;
      cache.factorContributionTotalZoom = null;
    });
    raDashboardDataService.setReportId(reportId);
    const jobs: Promise<unknown>[] = [];
    if (dataSources.has(RAReportDataSource.FactorContributionFreq)) {
      jobs.push(
        raDashboardDataService
          .getGfpaFactorContributionWithFreq(
            zoomPeriod.start,
            zoomPeriod.end,
            pickFactorFrequency(zoomPeriod.start, zoomPeriod.end),
          )
          .then(res => {
            if (cancelled) return;
            setZoomError(prev => prev || !!res?.error);
            setAttributionData(cache => {
              cache.factorContributionFreqZoom = (res?.data as IGfpaFactorContributionFreq[]) ?? [];
            });
          }),
      );
    }
    if (dataSources.has(RAReportDataSource.FactorContributionTotal)) {
      jobs.push(
        raDashboardDataService.getGfpaFactorContribution(zoomPeriod.start, zoomPeriod.end).then(res => {
          if (cancelled) return;
          setZoomError(prev => prev || !!res?.error);
          setAttributionData(cache => {
            cache.factorContributionTotalZoom = (res?.data as IGfpaFactorContributionTotal[]) ?? [];
          });
        }),
      );
    }
    Promise.all(jobs).finally(() => { if (!cancelled) setZoomLoading(false); });
    return () => { cancelled = true; };
  }, [reportId, zoomPeriod, dataSources]);

  return (
    <FAAttributionDataContext.Provider value={info}>
      {props.children}
    </FAAttributionDataContext.Provider>
  );
  function handleDataLoaded(res: AttributionAPIResult<unknown> | null) {
    if (res === null) return res;
    // The strip/table/legend read the totals and the chart reads the freq rows; a failure of either
    // must surface the card's error state (otherwise a failed fetch reads as "no data").
    if (res.resultKey === "factorContributionFreq" || res.resultKey === "factorContributionTotal") {
      setFactorFreqError(prev => prev || !!res.error);
    }
    if (res.resultKey === "securityContribution") {
      setSecurityError(!!res.error);
    }
    setAttributionData(cache => {
      switch (res.resultKey) {
        case "totalReturn":
          cache.totalReturn = res.data as AttributionTotalReturnsData;
          break;
        case "attrDurations":
          cache.attrDurations = res.data as AttributionDurationData;
          break;
        case "dailySummaries":
          cache.dailySummaries = {
            ...cache.dailySummaries,
            ...res.data as Record<string, AttributionGfpaSummaryDatum>
          };
          break;
        case "factorContributionFreq":
          cache.factorContributionFreq = res.data as IGfpaFactorContributionFreq[];
          break;
        case "factorContributionTotal":
          cache.factorContributionTotal = res.data as IGfpaFactorContributionTotal[];
          break;
        case "securityContribution":
          cache.securityContribution = res.data as IGfpaSecurityContrib[];
          break;
      }
    });
    return res;
  }
}

